import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, generatePath } from 'react-router-dom';
import { useIntl } from 'react-intl';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { setOpenAction } from 'domain/chat';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import Api from 'domain/api';
import { CloseButton, OpenButton, PreviewContent, PreviewImage, PreviewModal } from './StyledComponents';

interface Props {
  companyId: string;
  documentId: string;
  onClose: () => void;
  /**
   * Mobile view — hides the "Open" button. Opening the document navigates
   * within the app, which is unsupported in the mobile chat layout, so the
   * action is suppressed (mirrors the header open-in-new / company-link
   * mobile handling).
   */
  isMobile?: boolean;
}

const ChatDocumentPreviewModal: React.FC<Props> = ({ companyId, documentId, onClose, isMobile = false }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Close the preview and the whole chat drawer so the chat does not stay open
  // on top of the document the user navigates to.
  const handleOpen = () => {
    onClose();
    dispatch(setOpenAction(false));
  };

  const src = useMemo(
    () =>
      Api.getDocumentThumbnailUrl({
        companyId,
        documentID: documentId,
        preview: true,
        unsafe: true,
        t: Date.now(),
      }),
    [companyId, documentId],
  );

  // Sync the loaded flag whenever the previewed document changes. Reading the
  // image element's `complete` state (rather than blindly resetting to false)
  // avoids a race with cached thumbnails, where `onLoad` can fire before this
  // effect runs and would otherwise be overwritten, leaving the modal stuck in
  // the loading layout with no further `onLoad` to recover.
  useEffect(() => {
    setLoaded(Boolean(imgRef.current?.complete));
  }, [src]);

  const documentPath = useMemo(
    () => generatePath(ROUTES_PATH.DOCUMENT.absolute, { companyId, documentId }),
    [companyId, documentId],
  );

  return (
    <PreviewModal open onClose={onClose}>
      <PreviewContent loaded={loaded}>
        <CloseButton
          onClick={onClose}
          size="small"
          aria-label={formatMessage({ id: 'button.close', defaultMessage: 'Close' })}
        >
          <CloseOutlinedIcon fontSize="small" />
        </CloseButton>
        {/* No onError handler on purpose: if the thumbnail fails to load, `loaded`
            stays false so the reserved frame is kept and the Open/Close buttons stay
            anchored, rather than collapsing to the broken-image intrinsic size. */}
        <PreviewImage
          ref={imgRef}
          src={src}
          alt={formatMessage({ id: 'chat.previewDocument', defaultMessage: 'Preview document' })}
          onLoad={() => setLoaded(true)}
        />
        {!isMobile && (
          <OpenButton component={RouterLink} to={documentPath} onClick={handleOpen} variant="contained">
            {formatMessage({ id: 'contextMenu.open', defaultMessage: 'Open' })}
          </OpenButton>
        )}
      </PreviewContent>
    </PreviewModal>
  );
};

export default ChatDocumentPreviewModal;
